import {
  WorkflowAction,
  FieldUpdateConfig,
  EmailAlertConfig,
  TaskConfig,
  NotificationConfig,
  CreateRecordConfig,
  WebhookConfig,
  ActionExecutionResult
} from '../../types/workflowRules';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { getEmailTemplateById } from '../gobodo/gobodoEmailTemplateService';
import { buildVariablesFromContext, processEmailTemplate, type EmailTemplateContext } from '../../utils/emailTemplateUtils';
import { sendEmail } from '../emailSendService';
import type { Case } from '../../types/case';
import type { Client } from '../../types';
import { createRecord, updateRecord } from '../recordStorageService';
import { notificationService } from '../notificationService';

/**
 * Executes workflow actions.
 *
 * Updated to route through recordStorageService (Gobodo or localStorage)
 * instead of writing to localStorage directly. Email alerts are queued
 * until Michael's Gobodo email endpoint is available.
 */
export class ActionExecutor {

  async executeActions(
    actions: WorkflowAction[],
    record: any,
    objectApiName: string
  ): Promise<ActionExecutionResult[]> {
    const results: ActionExecutionResult[] = [];
    const sortedActions = [...actions].sort((a, b) => a.order - b.order);

    console.log(`⚡ Executing ${sortedActions.length} actions in sequence...`);

    for (const action of sortedActions) {
      try {
        const startTime = Date.now();
        await this.executeAction(action, record, objectApiName);
        const executionTime = Date.now() - startTime;

        results.push({
          actionId: action.id,
          actionType: action.type,
          success: true,
          executedAt: new Date().toISOString()
        });

        console.log(`✅ Action ${action.type} executed successfully (${executionTime}ms)`);
      } catch (error) {
        console.error(`❌ Action ${action.type} failed:`, error);

        results.push({
          actionId: action.id,
          actionType: action.type,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          executedAt: new Date().toISOString()
        });
      }
    }

    return results;
  }

  private async executeAction(
    action: WorkflowAction,
    record: any,
    objectApiName: string
  ): Promise<void> {
    switch (action.type) {
      case 'fieldUpdate':
        await this.executeFieldUpdate(action.config as FieldUpdateConfig, record, objectApiName);
        break;
      case 'emailAlert':
        await this.executeEmailAlert(action.config as EmailAlertConfig, record, objectApiName);
        break;
      case 'task':
        await this.executeTaskCreation(action.config as TaskConfig, record, objectApiName);
        break;
      case 'notification':
        await this.executeNotification(action.config as NotificationConfig, record, objectApiName);
        break;
      case 'createRecord':
        await this.executeCreateRecord(action.config as CreateRecordConfig, record, objectApiName);
        break;
      case 'webhook':
        await this.executeWebhook(action.config as WebhookConfig, record, objectApiName);
        break;
      default:
        throw new Error(`Unknown action type: ${(action as any).type}`);
    }
  }

  // ==========================================
  // ACTION TYPE IMPLEMENTATIONS
  // ==========================================

  /**
   * Field Update — uses recordStorageService so it goes through Gobodo
   * when enabled. Runs inside workflow context to prevent re-triggering.
   */
  private async executeFieldUpdate(
    config: FieldUpdateConfig,
    record: any,
    objectApiName: string
  ): Promise<void> {
    console.log(`📝 Field Update: ${config.fieldApiName} = ${config.newValue}`);

    // We're already inside workflowContext so the updateRecord call
    // in recordStorageService will see isInWorkflowContext() === true
    // and skip re-triggering workflows.
    try {
      await updateRecord(objectApiName, record.id, {
        [config.fieldApiName]: config.newValue,
      });
    } catch (err) {
      // Fallback: update in-memory record so subsequent actions see the new value
      console.warn('Field update via recordStorageService failed, updating in-memory:', err);
      record[config.fieldApiName] = config.newValue;
    }

    // Also update in-memory record for subsequent actions in this execution
    record[config.fieldApiName] = config.newValue;
    console.log(`✅ Field ${config.fieldApiName} updated to: ${config.newValue}`);
  }

  /**
   * Execute email alert action.
   * When Gobodo is enabled: resolves template by id, builds context from record,
   * composes subject/body with buildVariablesFromContext + processEmailTemplate, then sends via sendEmail.
   */
  private async executeEmailAlert(
    config: EmailAlertConfig,
    record: any,
    objectApiName: string
  ): Promise<void> {
    console.log(`📧 Email Alert: Template ${config.templateId}`);
    console.log(`   Recipients:`, config.recipients);

    const recipientNames = this.getRecipientNames(config.recipients, record);

    if (shouldUseGobodo()) {
      const template = await getEmailTemplateById(config.templateId);
      if (!template) {
        throw new Error(`Email template not found: ${config.templateId}`);
      }
      const context = this.buildEmailContextFromRecord(record, objectApiName);
      const variables = buildVariablesFromContext(context);
      const { subject, body } = processEmailTemplate(
        { subject: template.subject, bodyHtml: template.bodyHtml },
        variables
      );
      const toAddresses = this.resolveRecipientEmails(config, record);
      if (toAddresses.length === 0) {
        throw new Error('No recipient email addresses resolved for email alert');
      }
      await sendEmail({
        to: toAddresses,
        cc: config.ccEmails,
        bcc: config.bccEmails,
        subject,
        message: body,
      });
      console.log(`✅ Email sent to: ${toAddresses.join(', ')}`);
      return;
    }

    console.log(`✅ Email sent to: ${recipientNames.join(', ')} (mock)`);
  }

  /**
   * Build EmailTemplateContext from workflow record and object type.
   */
  private buildEmailContextFromRecord(record: any, objectApiName: string): EmailTemplateContext {
    const context: EmailTemplateContext = {
      system: {
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        url: typeof window !== 'undefined' ? window.location.origin : '',
        companyName: 'Product Hub',
      },
    };
    const r = record as Record<string, unknown>;
    if (objectApiName === 'Case__c' || objectApiName === 'Case') {
      context.case = {
        id: String(r.id ?? ''),
        caseNumber: String(r.CaseNumber__c ?? r.caseNumber ?? ''),
        subject: String(r.Subject__c ?? r.subject ?? ''),
        status: (r.Status__c ?? r.status ?? 'New') as Case['status'],
        priority: (r.Priority__c ?? r.priority ?? 'Medium') as Case['priority'],
        type: (r.Type__c ?? r.type ?? 'Question') as Case['type'],
        origin: (r.Origin__c ?? r.origin ?? 'Phone') as Case['origin'],
        contactName: String(r.ContactName__c ?? r.contactName ?? r.accountName ?? ''),
        accountName: String(r.AccountName__c ?? r.accountName ?? ''),
        owner: String(r.Owner__c ?? r.owner ?? ''),
        createdDate: String(r.CreatedDate__c ?? r.createdDate ?? ''),
        lastModifiedDate: String(r.LastModifiedDate__c ?? r.lastModifiedDate ?? ''),
        escalated: Boolean(r.Escalated__c ?? r.escalated),
      } as Case;
    } else if (objectApiName === 'Client__c' || objectApiName === 'Client' || objectApiName === 'CyberSweepClient__c') {
      context.client = {
        id: String(r.id ?? ''),
        name: String(r.Name__c ?? r.name ?? ''),
        email: String(r.Email__c ?? r.email ?? ''),
        phone: String(r.Phone__c ?? r.phone ?? ''),
        segment: String(r.Segment__c ?? r.segment ?? ''),
        status: String(r.Status__c ?? r.status ?? ''),
        owner: String(r.Owner__c ?? r.owner ?? ''),
        lastContact: String(r.LastContact__c ?? r.lastContact ?? ''),
        riskTier: String(r.RiskTier__c ?? r.riskTier ?? ''),
        accountName: String(r.AccountName__c ?? r.accountName ?? ''),
        accountOwner: String(r.AccountOwner__c ?? r.accountOwner ?? ''),
        accountManager: String(r.AccountManager__c ?? r.accountManager ?? ''),
      } as Client;
    }
    return context;
  }

  /**
   * Resolve recipient list to email addresses (for sendEmail to array).
   */
  private resolveRecipientEmails(config: EmailAlertConfig, record: any): string[] {
    const to: string[] = [...(config.additionalEmails ?? [])];
    const r = record as Record<string, unknown>;
    for (const recipient of config.recipients) {
      if (recipient.type === 'email') {
        to.push(recipient.email);
      } else if (recipient.type === 'recordOwner') {
        const email = r.OwnerEmail__c ?? r.ownerEmail ?? r.email;
        if (email) to.push(String(email));
      }
      // user / role: would require user/role lookup; skip if no email available
    }
    return Array.from(new Set(to));
  }

  /**
   * Task creation — routes through recordStorageService → Gobodo/localStorage.
   */
  private async executeTaskCreation(
    config: TaskConfig,
    record: any,
    objectApiName: string
  ): Promise<void> {
    console.log(`📋 Create Task: ${config.subject}`);

    const dueDate = this.calculateDueDate(config.dueDate);

    const taskData: Record<string, any> = {
      Subject__c: config.subject,
      Description__c: config.description || `Auto-created by workflow for ${objectApiName} record ${record.id}`,
      Owner__c: config.assignedTo,
      DueDate__c: dueDate,
      Priority__c: config.priority,
      Status__c: config.status || 'Open',
      RelatedTo__c: record.id,
      RelatedObjectType__c: objectApiName,
      Source__c: 'workflow_automation',
    };

    try {
      const created = await createRecord('Task', taskData);
      console.log(`✅ Task created: ${created.id}`);
    } catch (err) {
      console.warn('Task creation via recordStorageService failed, using localStorage fallback:', err);
      // Fallback to direct localStorage
      const tasks = JSON.parse(localStorage.getItem('crm_task_records') || '[]');
      tasks.push({
        ...taskData,
        id: `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdDate: new Date().toISOString(),
        createdBy: 'workflow_system',
      });
      localStorage.setItem('crm_task_records', JSON.stringify(tasks));
    }
  }

  /**
   * Notification — stores in the notification service (visible in NotificationBell).
   */
  private async executeNotification(
    config: NotificationConfig,
    record: any,
    objectApiName: string
  ): Promise<void> {
    console.log(`🔔 Notification: ${config.message}`);

    // Replace simple merge fields in the message
    let message = config.message;
    if (record) {
      Object.keys(record).forEach(key => {
        const val = record[key];
        if (typeof val === 'string' || typeof val === 'number') {
          message = message.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), String(val));
        }
      });
    }

    // Create a notification for each recipient
    for (const recipient of config.recipients) {
      notificationService.add({
        message,
        priority: config.priority,
        recipientType: recipient.type,
        recipientId: (recipient as any).userId || (recipient as any).roleId,
        actionUrl: config.actionUrl,
        objectApiName,
        recordId: record.id,
      });
    }

    // Browser notification if permitted
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      new Notification('Workflow Notification', {
        body: message,
        icon: '/favicon.ico',
      });
    }
  }

  /**
   * Create Record — routes through recordStorageService → Gobodo/localStorage.
   */
  private async executeCreateRecord(
    config: CreateRecordConfig,
    record: any,
    triggerObjectApiName: string
  ): Promise<void> {
    console.log(`➕ Create Record: ${config.objectApiName}`);

    const newRecordData: Record<string, any> = {
      Source__c: 'workflow_automation',
    };

    // Apply field mappings
    config.fieldMappings.forEach(mapping => {
      if (mapping.valueType === 'static') {
        newRecordData[mapping.fieldApiName] = mapping.value;
      } else if (mapping.valueType === 'triggerField') {
        newRecordData[mapping.fieldApiName] = record[mapping.value];
      }
    });

    // Link to trigger record
    if (config.linkToTriggerRecord) {
      newRecordData.RelatedTo__c = record.id;
      newRecordData.RelatedObjectType__c = triggerObjectApiName;
    }

    try {
      const created = await createRecord(config.objectApiName, newRecordData);
      console.log(`✅ ${config.objectApiName} record created: ${created.id}`);
    } catch (err) {
      console.warn('Record creation via recordStorageService failed, using localStorage fallback:', err);
      const storageKey = `crm_records_${config.objectApiName}`;
      const existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
      existing.push({
        ...newRecordData,
        id: `${config.objectApiName.toLowerCase()}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdDate: new Date().toISOString(),
        createdBy: 'workflow_system',
      });
      localStorage.setItem(storageKey, JSON.stringify(existing));
    }
  }

  /**
   * Webhook — actually executes the HTTP request now (with timeout).
   */
  private async executeWebhook(
    config: WebhookConfig,
    record: any,
    objectApiName: string
  ): Promise<void> {
    console.log(`🌐 Webhook: ${config.method} ${config.url}`);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(config.headers || {}),
    };

    let body: string | undefined;
    if (config.body && ['POST', 'PUT', 'PATCH'].includes(config.method)) {
      body = config.body;
      Object.keys(record).forEach(key => {
        body = body!.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), String(record[key] ?? ''));
      });
    } else if (config.includeRecordData && ['POST', 'PUT', 'PATCH'].includes(config.method)) {
      body = JSON.stringify({ record, objectApiName, timestamp: new Date().toISOString() });
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);

    try {
      const response = await fetch(config.url, {
        method: config.method,
        headers,
        body,
        signal: controller.signal,
      });
      console.log(`📤 Webhook response: ${response.status} ${response.statusText}`);
      if (!response.ok) {
        throw new Error(`Webhook returned ${response.status}`);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        throw new Error('Webhook timed out after 10s');
      }
      throw err;
    } finally {
      clearTimeout(timeout);
    }
  }

  // ==========================================
  // HELPERS
  // ==========================================

  private calculateDueDate(dueDate: TaskConfig['dueDate']): string {
    if (dueDate.type === 'relative') {
      const date = new Date();
      date.setDate(date.getDate() + dueDate.days);
      return date.toISOString();
    }
    return dueDate.date;
  }

  private getRecipientNames(recipients: any[], record: any): string[] {
    return recipients.map(r => {
      if (r.type === 'recordOwner') return record.owner || record.Owner__c || 'Record Owner';
      if (r.type === 'user') return r.userId || 'User';
      if (r.type === 'role') return r.roleId || 'Role';
      if (r.type === 'email') return r.email;
      return 'Unknown';
    });
  }
}

export const actionExecutor = new ActionExecutor();
